import PropTypes from "prop-types"
import { memo, useEffect, useRef } from "react"

import {
  ChoiceLabel,
  ChoiceSwatch,
  GradientContainer,
  OptionPrice,
  OptionTitle,
  OptionsSlider,
} from "areas/product/wizard-options/components"
import Image from "components/ui/image"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import useNavigation from "helpers/use-navigation"

const Stones = memo(
  ({ headingTitle, options, pref, selectedChoice, setChoice }) => {
    const sliderRef = useRef()

    useEffect(() => {
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = 0
      }
    }, [options])

    return (
      <GradientContainer shouldUseNewWizardExp>
        <OptionsSlider
          aria-label={`${headingTitle} Options`}
          className="desktop:grid grid-cols-4"
          data-slider="true"
          ref={sliderRef}
          role="radio-group"
          shouldUseNewWizardExp
        >
          {options.map((option) => {
            const selected =
              (option.jewlrValue || option.value) ===
              (selectedChoice?.jewlrValue || selectedChoice?.value)

            return (
              <StoneItem
                key={`${pref}-${option.jewlrValue || option.value}`}
                option={option}
                pref={pref}
                selected={selected}
                setChoice={setChoice}
              />
            )
          })}
        </OptionsSlider>
      </GradientContainer>
    )
  }
)

Stones.displayName = "Stones"

Stones.propTypes = {
  headingTitle: PropTypes.string,
  options: PropTypes.array,
  pref: PropTypes.string,
  selectedChoice: PropTypes.object,
  setChoice: PropTypes.func,
}

const StoneItem = memo(({ option, pref, selected, setChoice }) => {
  const keyboardProps = useNavigation({ direction: "both" })
  const itemRef = useRef()

  const stoneImage = imageLookup(
    pref === "as" ? "as" : "stone",
    option.imageKey
    // "front"
  )

  useEffect(() => {
    if (selected && itemRef.current) {
      const timer = setTimeout(() => {
        itemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        })
      }, 50)

      return () => clearTimeout(timer)
    }
  }, [selected])

  return (
    <ChoiceLabel
      className="shrink-0 grow-0 basis-1/4"
      data-cy={`product-wizard-option-${pref}-${option.jewlrValue}`}
      data-is-option="true"
      data-is-selected={selected}
      htmlFor={`pwo-${pref}-${option.jewlrValue}`}
      ref={itemRef}
    >
      <input
        aria-label={`${option.info || option.title}${option.price > 0 ? `, ${to_currency(option.price)}` : ""}`}
        checked={selected}
        className="peer sr-only"
        id={`pwo-${pref}-${option.jewlrValue}`}
        name={`pwo-${pref}`}
        onChange={() => setChoice(option)}
        type="radio"
        value={option.jewlrValue}
        {...keyboardProps}
      />
      <ChoiceSwatch>
        <Image
          className="size-36"
          data-sizes="auto"
          fallback={stoneImage.x2}
          height="40"
          srcSet={`${stoneImage.x1} 40w, ${stoneImage.x2} 80w`}
          width="40"
        />
      </ChoiceSwatch>
      <OptionTitle shouldUseNewWizardExp>
        {option.info || option.title}
      </OptionTitle>
      {option.price > 0 && (
        <OptionPrice shouldUseNewWizardExp>
          {to_currency(option.price)}
        </OptionPrice>
      )}
    </ChoiceLabel>
  )
})

StoneItem.displayName = "StoneItem"

StoneItem.propTypes = {
  option: PropTypes.object,
  pref: PropTypes.string,
  selected: PropTypes.bool,
  setChoice: PropTypes.func,
}

export default Stones
